import { Component, HostListener, computed, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { HeaderComponent } from '../header/header.component';
import { NavbarComponent } from '../navbar/navbar.component';
import { LoadingSpinnerComponent } from '../shared/loading-spinner.component';
import { ShuntingPassportCellComponent } from './shunting-passport-cell.component';
import { SearchService } from '../shared/search.service';
import { FilterService } from '../shared/filter.service';
import { PassportService, PassportRecord } from '../shared/passport.service';
import { PassportDetailsStore } from '../shared/passport-details.store';
import { UiStateService } from '../shared/ui-state.service';
import { OfflineStorageService } from '../shared/offline-storage.service';
import { CommentsNotificationService } from '../shared/comments-notification.service';
import { PassportHelperService } from '../shared/passport-helper.service';
import { PersonaRoutingService } from '../shared/persona-routing.service';
import { PassportCacheService } from '../shared/passport-cache.service';
import { SyncService } from '../shared/sync.service';
import { BaseStatusPersonaComponent } from '../shared/base-status-persona.component';
import { environment } from '../../environments/environment';
import { DateWithOrdinalPipe } from '../shared/date-with-ordinal.pipe';
import { NoPassportsComponent } from '../shared/no-passports.component';
import { HistoryPanelService } from '../shared/history-panel.service';
import { CommentsPanelService } from '../shared/comments-panel.service';
import { PassportMainWithDraftDto } from '../interfaces/api-responses';
import { DesktopStatusRowComponent, FormattedDateComponent, ActionButtonsComponent, DesktopPaginationComponent } from '../shared/desktop-table-templates.component';
import { DesktopEmptyStateComponent } from '../shared/desktop-empty-state.component';
import { PERSONA_NAVBAR_ITEMS } from '../shared/constants/navbar.constant';
import {
  clearShuntingListRestoreState,
  normalizeShuntingDesktopShowRows,
  parseShuntingListSnapshot,
  peekShuntingExpectRestore,
  saveShuntingListSnapshot,
  type ShuntingListSnapshot
} from './shunting-list-state.storage';

/**
 * Shunting persona component
 * Extends BaseStatusPersonaComponent with hardcoded receiver role
 */
@Component({
  selector: 'app-shunting',
  standalone: true,
  imports: [
    HeaderComponent,
    NavbarComponent,
    ShuntingPassportCellComponent,
    CommonModule,
    LoadingSpinnerComponent,
    DateWithOrdinalPipe,
    NoPassportsComponent,
    FormsModule,
    DesktopStatusRowComponent,
    FormattedDateComponent,
    ActionButtonsComponent,
    DesktopPaginationComponent,
    DesktopEmptyStateComponent
  ],
  templateUrl: './shunting.component.html',
  styleUrl: './shunting.component.css'
})
export class ShuntingComponent extends BaseStatusPersonaComponent implements OnDestroy {
  /** Navbar items constant for template */
  readonly navbarItems = PERSONA_NAVBAR_ITEMS;

  /** Applied in afterPassportsLoaded after full fetch when returning from passport details */
  private pendingShuntingRestore: ShuntingListSnapshot | null = null;
  private passportIdToScrollAfterRestore: string | null = null;

  shuntingFilters: Array<{ id: string; label: string; options: string[] }> = [];
  
  protected get personaName(): string {
    return 'shunting';
  }

  get headerFilters(): Array<{ id: string; label: string; options: string[] }> {
    return this.shuntingFilters;
  }

  constructor(
    http: HttpClient,
    passports: PassportService,
    searchService: SearchService,
    filterService: FilterService,
    router: Router,
    store: PassportDetailsStore,
    ui: UiStateService,
    syncService: SyncService,
    offlineStorage: OfflineStorageService,
    commentsNotification: CommentsNotificationService,
    helper: PassportHelperService,
    personaRouting: PersonaRoutingService,
    passportCache: PassportCacheService,
    private readonly historyPanelService: HistoryPanelService,
    private readonly commentsPanelService: CommentsPanelService
  ) {
    super(http, passports, searchService, filterService, router, store, ui, syncService, offlineStorage, commentsNotification, helper, personaRouting, passportCache);
  }

  override ngOnInit(): void {
    this.updateDesktopViewState();

    let restoring = peekShuntingExpectRestore();
    let snapshot = restoring ? parseShuntingListSnapshot() : null;
    if (restoring && !snapshot) {
      clearShuntingListRestoreState();
      restoring = false;
      snapshot = null;
    }
    this.pendingShuntingRestore = snapshot;

    if (!restoring) {
      this.filterService.clear();
      this.searchService.clear();
      this.ui.setSearchOpen(false);
      this.ui.setFiltersOpen(false);
      this.initializeMockRecords();
      this.generateFiltersFromRecords(this.passports.records());
    }

    super.ngOnInit();
  }

  protected override shouldSkipDataReload(): boolean {
    if (peekShuntingExpectRestore()) {
      return false;
    }
    return super.shouldSkipDataReload();
  }

  protected override afterScrollRestored(): void {
    this.tryScrollRestoredPassportIntoView();
  }

  protected override convertAndStorePassportMainData(passports: PassportMainWithDraftDto[]): void {
    // 1. Let base class process API records (this will overwrite current records)
    super.convertAndStorePassportMainData(passports);
    
    // 2. Re-generate filters with the combined set of records
    this.generateFiltersFromRecords(this.passports.records());
  }

  /**
   * Initialize mock shunting records for development/testing
   */
  private initializeMockRecords(): void {
    // All dummy data removed as per user request
    console.log('[Shunting] Initialized mock records: 0');
  }

  /**
   * Override to use Shunter role (RoleID 5)
   * Shunter gets passports filtered by their ShuntingYard via backend SP logic
   */
  protected override getApiUrl(): string {
    // For Shunter, use current user's ID and roleId from personaName
    // Backend SP will:
    // 1. Get ShuntingYardID from ShunterShuntingYard for this user
    // 2. Get PlantID from PlantShuntingYard
    // 3. Get Receiver UserID from ReceiverPlant
    // 4. Return passports filtered by ShuntingYardID
    const userId = localStorage.getItem('userID') || '';
    // FIX: Use component's personaName instead of hardcoded roleId to ensure consistency
    const persona = this.personaName; // Always use 'shunting' for shunting component
    const roleId = this.helper.getRoleIdForPersona(persona);
    console.log(`📋 Shunting loading passports with persona: '${persona}', roleId: ${roleId}`);
    return `${environment.apiBaseUrl}/api/v1/passports?userId=${userId}&roleId=${roleId}`;
  }

  /**
   * Override to store filters in shuntingFilters property
   * Filters are generated dynamically based on the values present in the records.
   * Uses all records (not filtered) to ensure filter options remain available
   */
  protected override generateFiltersFromRecords(records: PassportRecord[]): void {
    const sourceRecords = this.passports.records();
    const origins = new Set<string>();
    const destinations = new Set<string>();
    const apnNames = new Set<string>();
    let hasEmptyOrigin = false;

    for (const r of sourceRecords) {
      const details = this.store.get(r.id);
      
      // Check if this record has an empty origin (check both record and details)
      const originValue = r.origin || details?.origin;
      if (originValue?.trim()) {
        origins.add(originValue);
      } else {
        hasEmptyOrigin = true;
      }
      
      // Use weighbridge destination (from weighbridge data) with fallback to legacy destination
      const destinationValue = details?.destinationWeighbridge || r.destinationWeighbridge || r.destination;
      if (destinationValue) destinations.add(destinationValue);
      
      // Add APN/Name using apnName or apn
      const apnValue = r.apnName || r.apn;
      if (apnValue) apnNames.add(apnValue);
    }

    // Sort origin options, keeping "No Origin Field" first if applicable
    const sortedOrigins = Array.from(origins).sort((a, b) => a.localeCompare(b));
    const originOptions = hasEmptyOrigin ? [this.helper.NO_ORIGIN_FIELD_TEXT, ...sortedOrigins] : sortedOrigins;

    this.shuntingFilters = [
      { id: 'apnName', label: 'APN/Name', options: Array.from(apnNames).sort((a, b) => a.localeCompare(b)) },
      { id: 'destination', label: 'Destination', options: Array.from(destinations).sort((a, b) => a.localeCompare(b)) },
      { id: 'origin', label: 'Origin', options: originOptions }
    ];
  }

  /**
   * Memoized search + filter pipeline. Uses debounced `SearchService.filterQuery` so typing
   * stays responsive while list filtering runs after a short pause.
   */
  readonly filteredShuntingRecords = computed(() => {
    try {
      const all = this.passports.records();
      const query = (this.searchService.filterQuery() || '').trim().toLowerCase();
      const filters = this.filterService.filters();

      let filtered = all;

      if (query.length > 0) {
        filtered = filtered.filter(r => {
          const details = this.store.get(r.id);
          const originSearchValue = details?.origin?.trim() ? details.origin : this.helper.NO_ORIGIN_FIELD_TEXT;

          const searchable = [
            r.loadNumber || '',
            r.variety || '',
            r.haulier || '',
            r.bulker || '',
            r.growerName || '',
            details?.loadNumber || '',
            details?.vendorName || '',
            details?.vendorNumber || '',
            details?.variety || '',
            originSearchValue,
            details?.destinationWeighbridge || details?.destination || '',
            details?.haulier || '',
            details?.apnName || '',
            details?.bulkerNumber || '',
            this.dateTimeHelper.formatScheduledText(details?.dueDateTime || '')
          ]
            .join(' ')
            .toLowerCase();
          return searchable.includes(query);
        });
      }

      if (filters['origin']?.length) {
        filtered = filtered.filter(r => this.matchesFilter(filters, 'origin', r.origin));
      }
      if (filters['destination']?.length) {
        filtered = filtered.filter(r => {
          const details = this.store.get(r.id);
          const destinationValue = details?.destinationWeighbridge || r.destinationWeighbridge || r.destination;
          return this.matchesFilter(filters, 'destination', destinationValue);
        });
      }
      if (filters['apnName']?.length) {
        filtered = filtered.filter(r => {
          const val = r.apnName || r.apn;
          return this.matchesFilter(filters, 'apnName', val);
        });
      }

      return filtered;
    } catch {
      return [];
    }
  });

  get visibleRecords(): PassportRecord[] {
    return this.filteredShuntingRecords();
  }

  get totalCount(): number {
    return this.filteredShuntingRecords().length;
  }

  get pendingCount(): number {
    return this.filteredShuntingRecords().filter(r => r.status !== 'completed').length;
  }

  get completedCount(): number {
    return this.filteredShuntingRecords().filter(r => r.status === 'completed').length;
  }

  // Desktop View Helpers
  get totalPages(): number {
    return this.helper.getTotalPages(this.visibleRecords.length, this.itemsPerPage);
  }

  get sortedPaginatedRecords(): PassportRecord[] {
    // 1. Sort
    let records = [...this.visibleRecords];
    if (this.sortColumn) {
      records.sort((a, b) => {
        const aDetails = this.store.get(a.id);
        const bDetails = this.store.get(b.id);
        // Handle scheduled column specially - convert to timestamp for proper date sorting
        let { valA, valB } = this.helper.getSortValues(this.sortColumn, a, b, aDetails, bDetails);
        if (this.sortColumn === 'scheduled') {
          valA = new Date(valA || 0).getTime();
          valB = new Date(valB || 0).getTime();
        }
        return this.helper.compareValues(valA, valB, this.sortDirection);
      });
    }

    // 2. Paginate
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    return records.slice(startIndex, startIndex + this.itemsPerPage);
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.closeDropdowns();
  }

  openPassportDetails(id: string): void {
    const filtersRaw = this.filterService.filters();
    const filtersClone: Record<string, string[]> = {};
    for (const key of Object.keys(filtersRaw)) {
      const vals = filtersRaw[key];
      filtersClone[key] = vals ? [...vals] : [];
    }

    const scrollY =
      globalThis.window === undefined
        ? undefined
        : globalThis.window.scrollY || globalThis.document?.documentElement?.scrollTop || 0;

    const showRowsPerPage = normalizeShuntingDesktopShowRows(this.itemsPerPage);

    saveShuntingListSnapshot({
      version: 1,
      filters: filtersClone,
      searchQuery: this.searchService.query(),
      currentPage: this.currentPage,
      itemsPerPage: showRowsPerPage,
      sortColumn: this.sortColumn,
      sortDirection: this.sortDirection,
      passportId: id,
      scrollY: scrollY === 0 ? undefined : scrollY
    });

    this.ui.setExpandedRecord(id);
    this.scrollPositionService.saveScrollPosition('/shunting');
    this.scrollPositionService.setReturningFromDetail(true);
    void this.router.navigate(['/passport', id], { state: { from: 'shunting' } });
  }

  openHistory(id: string): void {
    const header = this.buildPanelHeader(id);
    if (header) {
      this.historyPanelService.openHistoryPanel(id, header);
    }
  }

  openComments(id: string): void {
    const header = this.buildPanelHeader(id);
    if (header) {
      this.commentsPanelService.openCommentsPanel(id, header);
    }
  }

  /**
   * Override to disable offline caching for desktop view
   */
  protected override async afterPassportsLoaded(headers: any, passports: any[]): Promise<void> {
    if (this.pendingShuntingRestore) {
      this.applyShuntingListRestore(this.pendingShuntingRestore);
      this.pendingShuntingRestore = null;
    }

    const isDesktop = window.innerWidth >= 768;
    if (isDesktop) {
      return;
    }
  }

  private applyShuntingListRestore(snapshot: ShuntingListSnapshot): void {
    this.filterService.clear();
    this.searchService.clear();
    for (const key of Object.keys(snapshot.filters)) {
      const values = snapshot.filters[key];
      if (values?.length) {
        this.filterService.setFilter(key, values);
      }
    }
    this.searchService.setQuery(snapshot.searchQuery);
    this.searchService.syncFilterQueryWithQuery();
    this.rowsDropdownOpen = false;
    // Assign directly — setItemsPerPage() resets currentPage to 1
    this.itemsPerPage = normalizeShuntingDesktopShowRows(snapshot.itemsPerPage);
    this.sortColumn = snapshot.sortColumn;
    this.sortDirection = snapshot.sortDirection;
    this.currentPage = snapshot.currentPage;
    const totalPages = this.helper.getTotalPages(this.visibleRecords.length, this.itemsPerPage);
    if (this.currentPage > totalPages) {
      this.currentPage = totalPages;
    }
    if (this.currentPage < 1) {
      this.currentPage = 1;
    }
    this.generateFiltersFromRecords(this.passports.records());
    this.passportIdToScrollAfterRestore = snapshot.passportId;
    clearShuntingListRestoreState();
  }

  ngOnDestroy(): void {
    this.searchService.cancelDebouncedFilterUpdate();
  }

  private tryScrollRestoredPassportIntoView(): void {
    const id = this.passportIdToScrollAfterRestore;
    if (!id) {
      return;
    }
    this.passportIdToScrollAfterRestore = null;
    if (globalThis.document === undefined) {
      return;
    }
    requestAnimationFrame(() => {
      const safeId = id.replaceAll('"', '');
      const candidates = globalThis.document.querySelectorAll(`[data-passport-id="${safeId}"]`);
      const el = Array.from(candidates).find(e => (e as HTMLElement).offsetParent !== null);
      el?.scrollIntoView({ block: 'nearest', behavior: 'instant' });
    });
  }
}
